// apps/client/src/pages/pipeline-tracker/tabs/QueueSection.tsx
//
// Operations > Overview, section 6: Days in queue.
//
// WHAT IT ANSWERS. How long the in-flight deals have been sitting with a lender, in each lender
// stage, cut two ways: by the analyst working the file and by the lender holding it. The figures
// come from the Performance snapshot the rest of the view reads: In flight is the Pipeline tab's
// In flight, the stage is the tenant's own stage mapping, and days in stage is the snapshot date
// minus the date the deal entered its current stage, whole days. Nothing here reads a clock.
//
// WHAT IT IS CAREFUL ABOUT. A deal with no stage-entered date is counted and said, never aged. A
// date after the snapshot is clamped to zero days and said. A deal carrying two analysts' labels
// counts for each and the table says so. The lender is recorded on a minority of deals at some
// practices, so the by-bank table states its coverage on every render and loudly below half, the
// same rule as the deal mix wheels on the Pipeline tab.

import React from 'react';
import { Landmark, Tags, UserSearch } from 'lucide-react';
import type {
  NoSnapshot,
  QueueByAnalyst,
  QueueByLender,
  QueueRow,
  QueueStageColumn,
  QueueStageFigures,
  QueueViewData,
} from '../../../hooks/pipelineTracker/useOperationsOverview';
import { Honesty, Pill, num } from './pipelineParts';
import { ROW_HOVER, TonePanel, prettyDate } from './operationsParts';

/** The figures per stage, in reading order: how many, then how long. */
const FIGURES: ReadonlyArray<{ key: keyof QueueStageFigures; label: string; days: boolean }> = [
  { key: 'deals', label: 'Deals', days: false },
  { key: 'medianDays', label: 'Median', days: true },
  { key: 'p75Days', label: 'p75', days: true },
  { key: 'oldestDays', label: 'Oldest', days: true },
];

const figure = (value: number | null, days: boolean): string => {
  if (value === null) return '–';
  return days ? `${num(value)}d` : num(value);
};

const StageCells: React.FC<{ stages: QueueStageFigures[]; columns: QueueStageColumn[]; strong?: boolean }> = ({
  stages, columns, strong,
}) => (
  <>
    {columns.map((column, i) =>
      FIGURES.map((f) => (
        <td
          key={`${column.key}-${f.key}`}
          className={`py-2 pr-3 text-right tabular-nums ${strong ? 'font-semibold text-gray-100' : 'text-gray-200'} ${f.key === 'deals' ? 'border-l border-gray-700/40 pl-3' : ''}`}
        >
          {column.present ? figure(stages[i][f.key], f.days) : <span className="text-gray-600">n/a</span>}
        </td>
      )))}
  </>
);

/**
 * One table: a label column, then for each lender stage its four figures under the stage's own
 * name. Columns are in the tenant's funnel order; a stage the taxonomy lacks is shown as n/a.
 */
const QueueTable: React.FC<{
  testId: string;
  firstColumn: string;
  columns: QueueStageColumn[];
  rows: QueueRow[];
  /** Rows after the named ones, in bold or muted, that reconcile the table to every queue deal. */
  footer: Array<{ key: string; label: string; stages: QueueStageFigures[]; strong?: boolean }>;
}> = ({ testId, firstColumn, columns, rows, footer }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[0.6875rem] uppercase tracking-wider text-gray-400">
          <th rowSpan={2} className="pb-2 pl-2 align-bottom font-medium">{firstColumn}</th>
          {columns.map((c) => (
            <th key={c.key} colSpan={FIGURES.length} className="border-l border-gray-700/40 pb-1 pl-3 text-left font-semibold text-gray-300">
              {c.label}
              {!c.present && <span className="ml-1.5 font-normal normal-case tracking-normal text-gray-500">not in your stage mapping</span>}
            </th>
          ))}
        </tr>
        <tr className="text-[0.6875rem] uppercase tracking-wider text-gray-500">
          {columns.map((c) =>
            FIGURES.map((f) => (
              <th key={`${c.key}-${f.key}`} className={`pb-2 pr-3 text-right font-medium ${f.key === 'deals' ? 'border-l border-gray-700/40 pl-3' : ''}`}>
                {f.label}
              </th>
            )))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className={`border-t border-gray-700/50 odd:bg-white/[0.015] ${ROW_HOVER}`}>
            <td className="py-2 pl-2 text-gray-200">{row.name}</td>
            <StageCells stages={row.stages} columns={columns} />
          </tr>
        ))}
        {footer.map((f) => (
          <tr key={f.key} className={f.strong ? 'border-t border-gray-600' : 'border-t border-gray-700/50'}>
            <td className={`py-2 pl-2 ${f.strong ? 'font-semibold text-gray-100' : 'text-gray-400'}`}>{f.label}</td>
            <StageCells stages={f.stages} columns={columns} strong={f.strong} />
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** What every figure on this panel is based on, said once under each table. */
const BasisNote: React.FC<{ data: QueueViewData; children?: React.ReactNode }> = ({ data, children }) => (
  <div className="mt-3 space-y-2">
    <Honesty>
      Based on the {num(data.deals)} in-flight {data.deals === 1 ? 'deal' : 'deals'} in the lender stages of the
      Performance export as at {prettyDate(data.asAtDate)}. Days are whole days since the deal entered its
      current stage. Median and p75 are the age of a real deal at that rank; Oldest is the longest wait.
      {data.missingDateDeals > 0 && ` ${num(data.missingDateDeals)} ${data.missingDateDeals === 1 ? 'deal has' : 'deals have'} no stage-entered date: counted in Deals, left out of the day figures.`}
      {data.futureDateDeals > 0 && ` ${num(data.futureDateDeals)} ${data.futureDateDeals === 1 ? 'deal is' : 'deals are'} dated after the snapshot and ${data.futureDateDeals === 1 ? 'counts' : 'count'} as 0 days.`}
    </Honesty>
    {children}
  </div>
);

const ByAnalyst: React.FC<{ data: QueueViewData; byAnalyst: QueueByAnalyst }> = ({ data, byAnalyst }) => {
  if (!byAnalyst.configured) {
    return (
      <TonePanel title="Days in queue by analyst" tone="brand" icon={UserSearch}>
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-100"
          data-testid="queue-analysts-not-set-up"
        >
          <Tags className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            Days in queue by analyst starts once the Salestrekker labels that mark an analyst on a deal are
            named. Name them under What each analyst is working, above.
          </span>
        </div>
      </TonePanel>
    );
  }
  return (
    <TonePanel
      title="Days in queue by analyst"
      tone="brand"
      icon={UserSearch}
      badge={<Pill tone="good">Live</Pill>}
      blurb="Per analyst label, how many in-flight deals sit in each lender stage and how long they have been there."
    >
      <QueueTable
        testId="queue-by-analyst"
        firstColumn="Analyst"
        columns={data.stages}
        rows={byAnalyst.rows}
        footer={[
          { key: 'labelled', label: 'Any analyst', stages: byAnalyst.labelled, strong: true },
          { key: 'unlabelled', label: 'No analyst label', stages: byAnalyst.unlabelled },
        ]}
      />
      <BasisNote data={data}>
        {byAnalyst.sharedDeals > 0 && (
          <Honesty>
            {num(byAnalyst.sharedDeals)} {byAnalyst.sharedDeals === 1 ? 'deal carries' : 'deals carry'} more than one
            analyst&apos;s label and {byAnalyst.sharedDeals === 1 ? 'counts' : 'count'} for each, so the analyst rows
            add up to more than Any analyst.
          </Honesty>
        )}
      </BasisNote>
    </TonePanel>
  );
};

const coverageLine = (data: QueueViewData, byLender: QueueByLender): string =>
  `Lender recorded on ${num(byLender.recordedDeals)} of ${num(data.deals)} ${data.deals === 1 ? 'deal' : 'deals'}`;

const ByLender: React.FC<{ data: QueueViewData; byLender: QueueByLender }> = ({ data, byLender }) => (
  <TonePanel
    title="Days in queue by bank"
    tone="violet"
    icon={Landmark}
    badge={
      <>
        <Pill tone="good">Live</Pill>
        {byLender.lowCoverage && byLender.coveragePct !== null && (
          <Pill tone="warn">{byLender.coveragePct}% coverage</Pill>
        )}
      </>
    }
    blurb="Per lender, how many in-flight deals sit in each lender stage and how long they have been there."
  >
    <p
      data-testid="queue-lender-coverage"
      data-low-coverage={byLender.lowCoverage ? 'true' : 'false'}
      className={`mb-3 text-xs ${byLender.lowCoverage ? 'font-semibold text-amber-300' : 'text-gray-400'}`}
    >
      {coverageLine(data, byLender)}
      {byLender.lowCoverage && '. The rest sit in the Not recorded row rather than being guessed.'}
    </p>
    <QueueTable
      testId="queue-by-lender"
      firstColumn="Lender"
      columns={data.stages}
      rows={byLender.rows}
      footer={[{ key: 'not-recorded', label: 'Not recorded', stages: byLender.notRecorded }]}
    />
    <BasisNote data={data}>
      <Honesty>
        The lender is the one recorded on the deal in the Performance export
        {byLender.salesAsAtDate !== null
          ? `, else in the Sales export of ${prettyDate(byLender.salesAsAtDate)}.`
          : '. No Sales export was uploaded with this snapshot, so there was no second source to fill a gap.'}
      </Honesty>
    </BasisNote>
  </TonePanel>
);

export interface QueueSectionProps {
  /** The response's queue part; undefined when the server predates it. */
  data: (({ status: 'ok' } & QueueViewData) | NoSnapshot) | undefined;
}

const QueueSection: React.FC<QueueSectionProps> = ({ data }) => {
  if (data === undefined || data.status !== 'ok') {
    const copy = data === undefined
      ? 'Days in queue is not available yet. Refresh the page to load it.'
      : 'No Performance export has been published yet. Days in queue reads its stage-entered dates.';
    return (
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <TonePanel title="Days in queue by analyst" tone="brand" icon={UserSearch}>
          <p className="text-sm text-gray-400">{copy}</p>
        </TonePanel>
        <TonePanel title="Days in queue by bank" tone="violet" icon={Landmark}>
          <p className="text-sm text-gray-400">{copy}</p>
        </TonePanel>
      </div>
    );
  }
  return (
    // Stacked, one table per row: each table carries twelve figure columns, and side by side the
    // stage headings would wrap into a width nobody can read.
    <div className="grid grid-cols-1 gap-4" data-testid="queue-tables">
      <ByAnalyst data={data} byAnalyst={data.byAnalyst} />
      <ByLender data={data} byLender={data.byLender} />
    </div>
  );
};

export default QueueSection;
